import { POSTGRES_MAX_INT } from "@sg/shared/src/util/numbers"
import { trackDeep } from "@solid-primitives/deep"
import { keyArray } from "@solid-primitives/keyed"
import { createEffect, DEV, on, onCleanup, untrack, type Accessor } from "solid-js"
import { Group, type Scene } from "three"
import { parse } from "valibot"
import type { Nullable } from "vite-node"
import type { Engine } from "../../core/engine/Engine.type"
import type { EntityGraphNode } from "../../core/engine/EntityGraphNode/EntityGraphNode.type"
import entityDisplayName from "../../core/entity/entityDisplayName"
import applyEntityRotationToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityRotationToNextObject3DFrame"
import { DegreeNumberSchema } from "../../core/entity/EntityHeading/DegreeNumber.type"
import type { EntityId } from "../../core/entity/EntityId.type"
import isEntityUnit from "../../core/entity/isEntityUnit"
import findById from "../../core/findById"
import { isPlainObjDeeplyEqual } from "../../isPlainObjDeeplyEqual"
import { leadingThrottleDebouncePromise } from "../../ldsh/leadingThrottleDebouncePromise"
import { VERTICES_PER_TILE } from "../consts"
import addEntityThreeModelDebugTextSprite from "../EntityThreeModel/addEntityThreeModelDebugTextSprite"
import createEntityThreeModel from "../EntityThreeModel/createEntityThreeModel"
import type { EntityThreeModel } from "../EntityThreeModel/EntityThreeModel.type"
import loadEntityThreeModelGLTF from "../EntityThreeModel/loadEntityThreeModelGLTF"
import updateEntityThreeModel from "../EntityThreeModel/updateEntityThreeModel"
import disposeRecursive from "./disposeRecursive"
import enableShadows from "./enableShadows"
import createStandardUnitTextureEffect from "../texture/StandardUnitTexture/createStandardUnitTextureEffect"

export type EntityThreeModelCollection = {
  group: Group
  ents: Map<EntityId, EntityThreeModel>
}

export default function createEntityThreeModelCollectionV2(
  engine: Engine,
  scene: Scene,
  getSourceEntityList: Accessor<Array<EntityGraphNode>>,
  groupName: string,
): EntityThreeModelCollection {
  const entityThreeModelMap = new Map<number, EntityThreeModel>()

  const entityCollectionGroup = new Group()
  entityCollectionGroup.name = groupName
  const modelsLoadingPromiseSet = new Set<ReturnType<typeof loadEntityThreeModelGLTF>>()
  scene.add(entityCollectionGroup)

  const mapped = keyArray(
    getSourceEntityList,
    (ent: EntityGraphNode) => ent.id,
    (getEntityNodeGraph) => {
      // console.log("mapped", getIndex(), getEntity().id, entityDisplayName(getEntity()))

      let obj: Nullable<Awaited<ReturnType<typeof loadEntityThreeModelGLTF>>> = null
      
      const entityThreeModel = createEntityThreeModel(getEntityNodeGraph())
      entityThreeModelMap.set(getEntityNodeGraph().id, entityThreeModel)

      let loadingModelPromise: Nullable<ReturnType<typeof loadEntityThreeModelGLTF>> = null

      async function makeOrUpdateThreeObj() {
        // console.log('makeOrUpdateThreeObj', getEntity().id)
        try {
          await loadingModelPromise

          const entNode = getEntityNodeGraph()

          loadingModelPromise = loadEntityThreeModelGLTF(engine, entNode)
          modelsLoadingPromiseSet.add(loadingModelPromise)
          const newObjValue = await loadingModelPromise
          modelsLoadingPromiseSet.delete(loadingModelPromise)
          newObjValue.scale.multiplyScalar(VERTICES_PER_TILE / 32)
          newObjValue.traverse(enableShadows)

          entityThreeModel.obj = newObjValue

          newObjValue.name = `${entityDisplayName(entNode)}_${entNode.id}`
          entityCollectionGroup.add(newObjValue)
          if (DEV) {
            parse(DegreeNumberSchema, entNode.raw().heading)
          }

          newObjValue.userData.heading = newObjValue.userData.targetHeading = untrack(entNode.raw).heading
          updateEntityThreeModel(entityThreeModel, entNode, engine)
          addEntityThreeModelDebugTextSprite(newObjValue, entNode)
          if (isEntityUnit(entNode)) {
            // addProcedurallyGeneratedEntityTextures(engine, newObjValue, entNode)
            createStandardUnitTextureEffect(engine, newObjValue, entNode)
          }
          // because we are making the object exist for the first time,
          // we'll use a large number for elapsedMs to jump straight to intended heading
          applyEntityRotationToNextObject3DFrame(newObjValue, POSTGRES_MAX_INT)

          // const prevValue = entityThreeModelMap.get(entityId)
          if (obj) {
            entityCollectionGroup.remove(obj)
            disposeRecursive(obj)
          }
          obj = newObjValue
          // entityThreeModelMap.set(entityId, entityThreeModel)
        } catch (err) {
          console.error(err)
        }
      }

      const makeOrUpdateThreeObjDebounced = leadingThrottleDebouncePromise(makeOrUpdateThreeObj, 100)

      // if the player_id or player's paints change, we must reload the model
      createEffect(() => {
        const player = findById(engine.state.players, getEntityNodeGraph().player_id())
        if (player) {
          trackDeep(player)
        }
        // untracked because async
        // makeOrUpdateThreeObj(pto)
        makeOrUpdateThreeObjDebounced()
      })

      function cleanupObject(obj: Awaited<ReturnType<typeof loadEntityThreeModelGLTF>>) {
        entityCollectionGroup.remove(obj)
        disposeRecursive(obj)
        obj.removeFromParent()
      }

      onCleanup(() => {
        loadingModelPromise?.then(cleanupObject)
      })

      // if the entity's state has changed, we must update the object3D
      createEffect(on(getEntityNodeGraph().raw, (ent, prevEnt) => {
        // const ent = getEntity()
        if (isPlainObjDeeplyEqual(prevEnt, ent)) {
          return
        }
        // console.warn('entityThreeModel.h !== newHash')
        // console.log('update.effect', ent.id, entityDisplayName(ent))
        // console.log('  - diff', prettyPlainObjectDiffText(entityThreeModel.ent, ent))

        // const entityThreeModel = entityThreeModelMap.get(ent.id)
        if (obj) {
          entityThreeModel.g++ // Mark as updated
          updateEntityThreeModel(entityThreeModel, getEntityNodeGraph(), engine)
        }
      }))
      return
    }
  )

  createEffect(mapped)

  onCleanup(() => {
    entityCollectionGroup.removeFromParent()
    disposeRecursive(entityCollectionGroup)

    modelsLoadingPromiseSet.forEach(async (p) => {
      try {
        const obj = await p
        // const { obj } = entityThreeModel
        if (obj) {
          disposeRecursive(obj)
          obj.removeFromParent()
        }
      } catch (err) {
        console.error(err)
      }
    })
  })

  return {
    group: entityCollectionGroup,
    ents: entityThreeModelMap,
  }
}
